import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar, MapPin, Users, Wallet, Share2,
  Check, ArrowLeft, Crown, Shield, Vote,
  Star, QrCode, UserCheck, UserX, DollarSign, MessageCircle,
  Camera, X as XIcon, Image as ImageIcon
} from "lucide-react";
import { format } from "date-fns";
import KrewProgressTracker from "@/components/KrewProgressTracker";
import CountdownTimer from "@/components/CountdownTimer";
import KrewAIChat from "@/components/KrewAIChat";
import SmartSuggestions from "@/components/SmartSuggestions";

const InfoCard = ({ icon: Icon, value, subValue, iconColor = "text-primary" }: any) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <Icon className={`mb-2 h-5 w-5 ${iconColor}`} />
    <p className="text-sm font-semibold text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{subValue}</p>
  </div>
);

const MemberCard = ({ member, krew, isCreator, onMarkAttendance }: any) => {
  const profile = member.profiles as any;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20 text-sm font-bold text-primary">
        {profile?.display_name?.charAt(0)?.toUpperCase() || "?"}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-foreground">{profile?.display_name || "Unknown"}</p>
          {member.user_id === krew.creator_id && <Crown className="h-3.5 w-3.5 text-secondary" />}
        </div>
        <p className="text-xs text-muted-foreground">Score: {profile?.reliability_score || 100}%</p>
      </div>
      {isCreator && krew.status === "booked" && member.status === "committed" && (
        <div className="flex gap-1">
          <button onClick={() => onMarkAttendance(member.id, "attended")} className="rounded-lg bg-green-500/10 p-2 text-green-400 hover:bg-green-500/20" title="Mark attended"><UserCheck className="h-4 w-4" /></button>
          <button onClick={() => onMarkAttendance(member.id, "no_show")} className="rounded-lg bg-destructive/10 p-2 text-destructive hover:bg-destructive/20" title="Mark no-show"><UserX className="h-4 w-4" /></button>
        </div>
      )}
      <span className={`rounded-full px-3 py-1 text-xs font-medium ${
        member.status === "committed" ? "bg-green-500/20 text-green-400" :
        member.status === "attended" ? "bg-primary/20 text-primary" :
        member.status === "no_show" ? "bg-destructive/20 text-destructive" :
        "bg-secondary/20 text-secondary"
      }`}>{member.status.replace("_", " ")}</span>
    </div>
  );
};

const VenueVoteCard = ({ venue, votes, userId, onVote }: any) => {
  const venueVotes = votes.filter((v: any) => v.venue_id === venue.id);
  const hasVoted = venueVotes.some((v: any) => v.user_id === userId);
  return (
    <motion.button onClick={() => onVote(venue.id)} whileTap={{ scale: 0.98 }}
      className={`group overflow-hidden rounded-2xl border text-left transition-all ${hasVoted ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/30"}`}>
      <div className="aspect-video w-full overflow-hidden">
        <img src={venue.image_url || "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?w=400"} alt={venue.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">{venue.name}</h3>
          <div className="flex items-center gap-1 text-sm">
            <Vote className={`h-4 w-4 ${hasVoted ? "text-primary" : "text-muted-foreground"}`} />
            <span className={hasVoted ? "font-bold text-primary" : "text-muted-foreground"}>{venueVotes.length}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{venue.area} · ₹{venue.avg_price_per_person}/person</p>
        {venue.match_score && <p className="mt-1 text-xs text-primary">{venue.match_score}% match — {venue.ai_match_reason}</p>}
        <div className="mt-2 flex items-center gap-1"><Star className="h-3.5 w-3.5 text-secondary" /><span className="text-xs font-medium text-foreground">{venue.rating}</span></div>
      </div>
    </motion.button>
  );
};

const KrewDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const [krew, setKrew] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [venues, setVenues] = useState<any[]>([]);
  const [votes, setVotes] = useState<any[]>([]);
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [settling, setSettling] = useState(false);
  const [photos, setPhotos] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshMembers = async () => {
    if (!id) return;
    const { data } = await supabase.from("krew_members").select("*, profiles:user_id(display_name, avatar_url, reliability_score)").eq("krew_id", id);
    if (data) setMembers(data);
  };

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      const [krewRes, membersRes, votesRes, bookingRes] = await Promise.all([
        supabase.from("krews").select("*").eq("id", id).single(),
        supabase.from("krew_members").select("*, profiles:user_id(display_name, avatar_url, reliability_score)").eq("krew_id", id),
        supabase.from("venue_votes").select("*").eq("krew_id", id),
        supabase.from("bookings").select("*, venues(name, address, image_url)").eq("krew_id", id).maybeSingle(),
      ]);
      if (krewRes.data) {
        setKrew(krewRes.data);
        // Fetch AI-recommended venues instead of generic ones
        try {
          const { data: aiData } = await supabase.functions.invoke("ai-venue-recommend", {
            body: {
              vibes: krewRes.data.vibe || [],
              budget: krewRes.data.budget_per_person,
              groupSize: membersRes.data?.length || 4,
              city: krewRes.data.city,
              area: krewRes.data.area,
            },
          });
          if (aiData?.venues) setVenues(aiData.venues);
          else {
            const { data: fallback } = await supabase.from("venues").select("*").eq("city", krewRes.data.city).limit(6);
            if (fallback) setVenues(fallback);
          }
        } catch {
          const { data: fallback } = await supabase.from("venues").select("*").eq("city", krewRes.data.city).limit(6);
          if (fallback) setVenues(fallback);
        }
      }
      if (membersRes.data) setMembers(membersRes.data);
      if (votesRes.data) setVotes(votesRes.data);
      if (bookingRes.data) setBooking(bookingRes.data);
      // Fetch photos and resolve signed URLs
      const { data: photosData } = await supabase.from("krew_photos").select("*, profiles:user_id(display_name)").eq("krew_id", id).order("created_at", { ascending: false });
      if (photosData && photosData.length > 0) {
        const resolvedPhotos = await Promise.all(
          photosData.map(async (photo: any) => {
            // If image_url is already a full URL (legacy), keep it; otherwise get signed URL
            if (photo.image_url.startsWith("http")) return photo;
            const { data: signedData } = await supabase.storage.from("krew-photos").createSignedUrl(photo.image_url, 3600);
            return { ...photo, _storagePath: photo.image_url, image_url: signedData?.signedUrl || photo.image_url };
          })
        );
        setPhotos(resolvedPhotos);
      } else {
        setPhotos([]);
      }
      setLoading(false);
    };
    fetchData();

    const channel = supabase.channel(`krew-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "krew_members", filter: `krew_id=eq.${id}` }, () => refreshMembers())
      .on("postgres_changes", { event: "*", schema: "public", table: "venue_votes", filter: `krew_id=eq.${id}` }, async () => {
        const { data } = await supabase.from("venue_votes").select("*").eq("krew_id", id);
        if (data) setVotes(data);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [id]);

  const copyInviteLink = () => {
    const url = `${window.location.origin}/join/${krew?.invite_code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast({ title: "Link copied!" });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const url = `${window.location.origin}/join/${krew?.invite_code}`;
    const text = `🎉 Join my Krew "${krew.title}" on ${format(new Date(krew.event_date), "MMM d")} at ${krew.event_time?.slice(0, 5)}!\n\n💰 Budget: ₹${krew.budget_per_person}/person\n📍 ${krew.area || krew.city}\n\nJoin here: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const handleVote = async (venueId: string) => {
    if (!user || !id) return;
    const existing = votes.find((v) => v.user_id === user.id && v.venue_id === venueId);
    if (existing) {
      await supabase.from("venue_votes").delete().eq("id", existing.id);
      setVotes(votes.filter((v) => v.id !== existing.id));
    } else {
      const { data } = await supabase.from("venue_votes").insert({ krew_id: id, venue_id: venueId, user_id: user.id }).select().single();
      if (data) setVotes([...votes, data]);
    }
  };

  const handleCommit = async () => {
    if (!user || !id || !krew) return;
    try {
      const { error } = await supabase.rpc("commit_to_krew", { p_krew_id: id });
      if (error) throw error;
      toast({ title: "Committed! 🔒", description: `₹${krew.commitment_amount} deposit locked.` });
      refreshMembers();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to commit.", variant: "destructive" });
    }
  };

  const handleMarkAttendance = async (memberId: string, status: "attended" | "no_show") => {
    await supabase.from("krew_members").update({ status }).eq("id", memberId);
    toast({ title: status === "attended" ? "Marked attended ✅" : "Marked no-show ❌" });
    refreshMembers();
  };

  const handleSettle = async () => {
    if (!user || !id || !krew) return;
    setSettling(true);
    try {
      const { error } = await supabase.rpc("settle_krew", { p_krew_id: id });
      if (error) throw error;
      setKrew({ ...krew, status: "completed", is_settled: true });
      const attendedCount = members.filter((m) => m.status === "attended").length;
      const noShowCount = members.filter((m) => m.status === "no_show").length;
      toast({ title: "Krew settled! 🎉", description: `${attendedCount} refunded, ${noShowCount} penalized.` });
      refreshMembers();
    } catch (error: any) {
      toast({ title: "Error", description: "Settlement failed. Please try again.", variant: "destructive" });
    } finally { setSettling(false); }
  };

  if (loading) return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );

  if (!krew) return <div className="py-20 text-center text-muted-foreground">Krew not found</div>;

  const isCreator = user?.id === krew.creator_id;
  const currentMember = members.find((m) => m.user_id === user?.id);
  const committedCount = members.filter((m) => m.status === "committed").length;
  const allMarked = members.filter((m) => m.status === "committed").length === 0 &&
    members.filter((m) => ["attended", "no_show"].includes(m.status)).length > 0;
  const deadlinePassed = krew.commitment_deadline && new Date(krew.commitment_deadline) < new Date();

  return (
    <div className="space-y-8">
      <button onClick={() => window.history.back()} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Progress Tracker */}
      <KrewProgressTracker status={krew.status} />

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <h1 className="text-3xl font-bold text-foreground">{krew.title}</h1>
            {isCreator && <Crown className="h-5 w-5 text-secondary" />}
          </div>
          {krew.description && <p className="text-muted-foreground font-body">{krew.description}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={shareWhatsApp} className="flex items-center gap-2 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm font-semibold text-green-400 hover:bg-green-500/20">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </button>
          <button onClick={copyInviteLink} className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-5 py-3 text-sm font-semibold text-primary hover:bg-primary/20">
            {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      </div>

      {/* Countdowns */}
      <div className="space-y-2">
        <CountdownTimer targetDate={krew.event_date} targetTime={krew.event_time} label="Event starts in" />
        {krew.commitment_deadline && !deadlinePassed && (
          <CountdownTimer targetDate={krew.commitment_deadline.split("T")[0]} targetTime={krew.commitment_deadline.split("T")[1]?.slice(0, 8)} label="Commit deadline" />
        )}
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <InfoCard icon={Calendar} value={format(new Date(krew.event_date), "EEE, MMM d")} subValue={krew.event_time?.slice(0, 5)} />
        <InfoCard icon={MapPin} value={krew.area || krew.city} subValue={krew.city} />
        <InfoCard icon={Users} value={`${committedCount}/${krew.max_members}`} subValue="Committed" />
        <InfoCard icon={Wallet} value={`₹${krew.budget_per_person.toLocaleString()}`} subValue="Per person" iconColor="text-secondary" />
      </div>

      {/* Commitment CTA */}
      {currentMember && currentMember.status === "invited" && !deadlinePassed && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center">
          <Shield className="mx-auto mb-3 h-8 w-8 text-primary" />
          <h3 className="mb-1 text-lg font-bold text-foreground">Lock Your Commitment</h3>
          <p className="mb-4 text-sm text-muted-foreground">Deposit ₹{krew.commitment_amount} — refundable if you show up.</p>
          <button onClick={handleCommit} className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground glow-neon hover:scale-[1.02] transition-all">
            Commit ₹{krew.commitment_amount} <Shield className="h-4 w-4" />
          </button>
        </motion.div>
      )}

      {deadlinePassed && currentMember?.status === "invited" && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">The commitment deadline has passed. You can no longer commit to this Krew.</p>
        </div>
      )}

      {/* Settlement CTA */}
      {isCreator && krew.status === "booked" && allMarked && !krew.is_settled && (
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-2xl border border-secondary/30 bg-secondary/5 p-6 text-center">
          <DollarSign className="mx-auto mb-3 h-8 w-8 text-secondary" />
          <h3 className="mb-1 text-lg font-bold text-foreground">Ready to Settle</h3>
          <p className="mb-4 text-sm text-muted-foreground">Settle to refund attendees and penalize no-shows.</p>
          <button onClick={handleSettle} disabled={settling} className="inline-flex items-center gap-2 rounded-xl bg-secondary px-8 py-3 text-sm font-semibold text-secondary-foreground glow-gold hover:scale-[1.02] transition-all disabled:opacity-50">
            {settling ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-secondary-foreground border-t-transparent" /> : <>Settle Krew <DollarSign className="h-4 w-4" /></>}
          </button>
        </motion.div>
      )}

      {/* Members */}
      <section>
        <h2 className="mb-4 text-xl font-bold text-foreground">Krew Members ({members.length})</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {members.map((member) => (
            <MemberCard key={member.id} member={member} krew={krew} isCreator={isCreator} onMarkAttendance={handleMarkAttendance} />
          ))}
        </div>
      </section>

      {/* Smart suggestions (creator only, while still planning) */}
      {isCreator && (krew.status === "planning" || krew.status === "venue_voting") && members.length < krew.max_members && (
        <SmartSuggestions krewId={krew.id} isCreator={isCreator} />
      )}

      {/* Venue Voting */}
      {(krew.status === "planning" || krew.status === "venue_voting") && !krew.selected_venue_id && (
        <section>
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-xl font-bold text-foreground">Vote for a Venue</h2>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">AI Ranked</span>
          </div>
          <p className="mb-4 text-sm text-muted-foreground">Venues ranked by AI match score — tap to vote!</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {venues.map((venue) => (
              <VenueVoteCard key={venue.id} venue={venue} votes={votes} userId={user?.id} onVote={handleVote} />
            ))}
          </div>
        </section>
      )}

      {/* Booking Info */}
      {booking && (
        <section className="rounded-2xl border border-green-500/30 bg-green-500/5 p-6">
          <div className="flex items-center gap-3 mb-4">
            <QrCode className="h-6 w-6 text-green-400" />
            <h2 className="text-xl font-bold text-foreground">Booking Confirmed</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-muted-foreground">Venue</p><p className="font-semibold text-foreground">{(booking.venues as any)?.name}</p></div>
            <div><p className="text-muted-foreground">Table</p><p className="font-semibold text-foreground">{booking.table_number || "TBD"}</p></div>
            <div><p className="text-muted-foreground">Confirmation</p><p className="font-mono font-semibold text-primary">{booking.confirmation_code}</p></div>
            <div><p className="text-muted-foreground">Party Size</p><p className="font-semibold text-foreground">{booking.party_size} people</p></div>
          </div>
        </section>
      )}

      {/* Photo Gallery & Upload */}
      {(krew.status === "booked" || krew.status === "completed") && currentMember && (
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-foreground">📸 Night Photos</h2>
            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/20 transition-all">
              <Camera className="h-4 w-4" /> Upload
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !user || !id) return;
                  setUploading(true);
                  try {
                    const ext = file.name.split(".").pop();
                    const path = `${id}/${Date.now()}.${ext}`;
                    const { error: uploadErr } = await supabase.storage.from("krew-photos").upload(path, file);
                    if (uploadErr) throw uploadErr;
                    // Store the storage path, not public URL — photos are served via signed URLs
                    const { error: insertErr } = await supabase.from("krew_photos").insert({
                      krew_id: id,
                      user_id: user.id,
                      image_url: path,
                      caption: caption || null,
                    });
                    if (insertErr) throw insertErr;
                    toast({ title: "Photo uploaded! 📸" });
                    setCaption("");
                    const { data: newPhotos } = await supabase.from("krew_photos").select("*, profiles:user_id(display_name)").eq("krew_id", id).order("created_at", { ascending: false });
                    if (newPhotos) {
                      const resolved = await Promise.all(
                        newPhotos.map(async (p: any) => {
                          if (p.image_url.startsWith("http")) return p;
                          const { data: s } = await supabase.storage.from("krew-photos").createSignedUrl(p.image_url, 3600);
                          return { ...p, _storagePath: p.image_url, image_url: s?.signedUrl || p.image_url };
                        })
                      );
                      setPhotos(resolved);
                    }
                  } catch (err: any) {
                    toast({ title: "Upload failed", description: err.message, variant: "destructive" });
                  } finally {
                    setUploading(false);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }
                }}
              />
            </label>
          </div>
          {uploading && (
            <div className="mb-4 flex items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="text-sm text-primary">Uploading...</span>
            </div>
          )}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {photos.map((photo) => (
                <div key={photo.id} className="group relative overflow-hidden rounded-xl border border-border">
                  <img src={photo.image_url} alt={photo.caption || "Krew photo"} className="aspect-square w-full object-cover" loading="lazy" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-xs text-white">{(photo.profiles as any)?.display_name}</p>
                    {photo.caption && <p className="text-xs text-white/70">{photo.caption}</p>}
                  </div>
                  {photo.user_id === user?.id && (
                    <button
                      onClick={async () => {
                        // Delete storage file first (policy requires ownership via krew_photos table)
                        const originalPath = (photo as any)._storagePath || photo.image_url;
                        if (originalPath && !originalPath.startsWith("http")) {
                          await supabase.storage.from("krew-photos").remove([originalPath]);
                        }
                        await supabase.from("krew_photos").delete().eq("id", photo.id);
                        setPhotos(photos.filter(p => p.id !== photo.id));
                        toast({ title: "Photo removed" });
                      }}
                      className="absolute right-2 top-2 hidden rounded-full bg-destructive/80 p-1 text-destructive-foreground group-hover:block"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center">
              <ImageIcon className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">No photos yet — upload the first one!</p>
            </div>
          )}
        </section>
      )}

      {/* AI Chat */}
      <KrewAIChat context={{
        krewTitle: krew.title,
        city: krew.city,
        area: krew.area || undefined,
        eventDate: krew.event_date,
        eventTime: krew.event_time,
        budget: krew.budget_per_person,
        vibes: krew.vibe || [],
        memberCount: members.length,
      }} />
    </div>
  );
};

export default KrewDetail;
